import {
    Component,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    Renderer2,
    ViewChild,
} from '@angular/core';

@Component({
    selector: 'mobile-drawer',
    templateUrl: './mobile-drawer.component.html',
    styleUrl: './mobile-drawer.component.scss',
    standalone: false
})
export class MobileDrawerComponent {
    private readonly minHeight: number = 80;
    private initialHeight: number;
    @Input() isDrawerOpen: boolean;
    @Output() onOutsideClick: EventEmitter<any> = new EventEmitter();
    @Input() isFullScreen: boolean = false;
    @Input() showCloseIcon: boolean = false;
    @Input() isDraggable: boolean = false;
    @Input() isMinimized: boolean = false;
    @Output() isMinimizedChange: EventEmitter<boolean> = new EventEmitter();
    @ViewChild('drawerHandle', { static: false }) drawerHandle: ElementRef;

    private isDragging: boolean = false;
    private startY: number = 0;
    private startHeight: number = 0;

    private componentId: string;
    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {
        this.componentId =
            'uniqode-drawer-' + Math.random().toString(36).substr(2, 9);
    }

    ngOnChanges() {
        if (this.isDrawerOpen) {
            this.setOverflowHidden();
        } else {
            this.resetOverflow();
        }
    }

    private setOverflowHidden() {
        const appElement = this.el.nativeElement.closest('.app-root');
        const overflowHiddenClass = 'overflow-hidden';
        if (
            appElement &&
            !appElement?.hasAttribute(`uniqode-drawer-id="${this.componentId}"`)
        ) {
            this.renderer.addClass(appElement, overflowHiddenClass);
            appElement.setAttribute('uniqode-drawer-id', this.componentId);
        }
    }

    private resetOverflow() {
        const appElement = this.el.nativeElement.closest('.app-root');
        const overflowHiddenClass = 'overflow-hidden';
        if (
            appElement &&
            appElement?.hasAttribute(`uniqode-drawer-id="${this.componentId}"`)
        ) {
            this.renderer.removeClass(appElement, overflowHiddenClass);
            appElement.removeAttribute('uniqode-drawer-id');
        } else {
            this.forceResetOverflow();
        }
    }

    forceResetOverflow() {
        const appElement = this.el.nativeElement.closest('.app-root');
        const overflowHiddenClass = 'overflow-hidden';
        if (appElement) {
            this.renderer.removeClass(appElement, overflowHiddenClass);
        }
        
        if (this.isDraggable && this.drawerHandle?.nativeElement) {
            const drawerModal = this.drawerHandle.nativeElement.closest('.drawer-modal');
            if (drawerModal) {
                this.renderer.setStyle(drawerModal, 'transition', 'height 0.3s ease-in-out');
                this.renderer.setStyle(drawerModal, 'height', `${this.minHeight}px`);
                this.isMinimized = true;
                this.isMinimizedChange.emit(this.isMinimized);
            }
        }
    }

    onHandleMouseDown(event: MouseEvent) {
        if (!this.isDraggable) return;
        this.startDragging(event.clientY);
        
        const onMouseMove = (e: MouseEvent) => {
            if (this.isDragging) {
                this.onDrag(e.clientY);
            }
        };
        
        const onMouseUp = () => {
            this.stopDragging();
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }

    onHandleTouchStart(event: TouchEvent) {
        if (!this.isDraggable) return;
        event.preventDefault();
        event.stopPropagation();
        
        const touch = event.touches[0];
        this.startDragging(touch.clientY);
        
        const onTouchMove = (e: TouchEvent) => {
            if (this.isDragging) {
                e.preventDefault();
                e.stopPropagation();
                const currentTouch = e.touches[0];
                this.onDrag(currentTouch.clientY);
            }
        };
        
        const onTouchEnd = (e: TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();
            this.stopDragging();
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
        
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
    }

    ngAfterViewInit() {
        const drawerModal = this.drawerHandle.nativeElement.closest('.drawer-modal');
        this.initialHeight = drawerModal.offsetHeight;
    }

    private startDragging(clientY: number) {
        this.isDragging = true;
        this.startY = clientY;
        const drawerModal = this.drawerHandle.nativeElement.closest('.drawer-modal');
        this.startHeight = drawerModal.offsetHeight;
    }

    private onDrag(clientY: number) {
        if (!this.isDragging) return;
        
        const deltaY = clientY - this.startY;
        let newHeight = this.startHeight - deltaY;
        newHeight = Math.max(this.minHeight, Math.min(newHeight, this.initialHeight));
        
        const drawerModal = this.drawerHandle.nativeElement.closest('.drawer-modal');
        if (drawerModal) {
            this.renderer.setStyle(drawerModal, 'height', `${newHeight}px`);
            this.renderer.setStyle(drawerModal, 'transition', 'none');
            
            const shouldBeMinimized = newHeight <= (this.minHeight + 30);
            if (shouldBeMinimized !== this.isMinimized) {
                this.isMinimized = shouldBeMinimized;
                this.isMinimizedChange.emit(this.isMinimized);
            }
        }
    }

    private stopDragging() {
        this.isDragging = false;
        const drawerModal = this.drawerHandle.nativeElement.closest('.drawer-modal');
        if (drawerModal) {
            this.renderer.setStyle(drawerModal, 'transition', 'height 0.3s ease-in-out');
            
            // Snap to either minimized or full height
            const currentHeight = drawerModal.offsetHeight;
            const snapThreshold = this.minHeight + (this.initialHeight - this.minHeight) / 2;
            
            if (currentHeight < snapThreshold) {
                this.renderer.setStyle(drawerModal, 'height', `${this.minHeight}px`);
                this.isMinimized = true;
            } else {
                this.renderer.setStyle(drawerModal, 'height', `${this.initialHeight}px`);
                this.isMinimized = false;
            }
            this.isMinimizedChange.emit(this.isMinimized);
        }
    }

    onHandleKeyDown(event: KeyboardEvent) {
        if (!this.isDraggable) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.isMinimized = false;
            this.isMinimizedChange.emit(this.isMinimized);
        }
    }

    expandDrawer() {
        if (!this.isDraggable || this.isDragging) return;
        this.isMinimized = false;
        this.isMinimizedChange.emit(this.isMinimized);
    }
}
